import { InputComponentType, quickformtokens, useQuickForm } from "@eavfw/quickform-core";
import { makeStyles } from "@griffel/react";
import { mergeClasses, shorthands } from "@fluentui/react-components";
import { useState } from "react";


export type ImageGridProperties = {
    inputType: "image-grid";
    options: {
        [key: string]: {
            key: string;
            label: string;
            imageUrl: string;
            imageHash: string;
        }
    };
    customImagesPerRow?: boolean;
    imagesPerRowDesktop?: number;
    imagesPerRowTablet?: number;
    imagesPerRowPhone?: number;
    defaultValue?: string;
}


const useImageGridStyles = makeStyles({
    grid: {
        display: 'grid',
        //gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: "5px",
    },
    gridElement: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
        marginTop: "5px",
        background: "#FFF",
        border: "1px solid #ccc",
        //     height:"150px",
        position: "relative"

    },
    img: {
        maxHeight: "100%",
        maxWidth: "100%",
        //  position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        margin: "auto",
    },
    selected: {

    },
    label: {
        height: "var(--imagegrid-label-height, 55px)",
        minHeight: "var(--imagegrid-label-height, 55px)",
        backgroundColor: `${quickformtokens?.surface ?? "grey"}`,
        color: `${quickformtokens?.onSurface ?? "white"}`,
        fontSize: '1rem',
        cursor: 'pointer',
        flexGrow: 0,
        flexBasis: "100%",
        width: 'calc(100% - 1rem)',
        margin: '0.5rem',
        boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
        ...shorthands.transition(
            "background, color, border, border-color, box-shadow",
            "350ms",
            "ease-in-out",
            "0ms"
        ),
        ...shorthands.border("1px", "solid", "#dddddd"),
        borderRadius: "5px",
    },
    selectedlabel: {
        backgroundColor: `${quickformtokens?.primary ?? "grey"}`,
        color: `${quickformtokens?.onPrimary ?? "white"}`,
    }
});

const getLabelHeight = (imagesPerRow: number, breakpoint: "desktop" | "tablet" | "phone") => {
    if (breakpoint === "phone") {
        if (imagesPerRow >= 8) return "18px";
        if (imagesPerRow >= 6) return "22px";
        if (imagesPerRow >= 4) return "28px";
        return "36px";
    }
    if (breakpoint === "tablet") {
        if (imagesPerRow >= 8) return "22px";
        if (imagesPerRow >= 6) return "28px";
        if (imagesPerRow >= 4) return "36px";
        return "44px";
    }
    // desktop
    if (imagesPerRow >= 10) return "28px";
    if (imagesPerRow >= 8) return "36px";
    if (imagesPerRow >= 6) return "44px";
    return "55px";
};


export const ImageGrid: InputComponentType<ImageGridProperties> = ({
    options,
    questionModel,
    customImagesPerRow = false,
    imagesPerRowDesktop = 4,
    imagesPerRowTablet = 4,
    imagesPerRowPhone = 2
}) => {
    const { answerQuestion } = useQuickForm();
    const styles = useImageGridStyles();
    const [selectedValue, setSelectedValue] = useState<string>(questionModel.output);

    const gridStyle: React.CSSProperties = customImagesPerRow
        ? { display: 'grid', gap: '5px' }
        : { display: 'grid', gap: '5px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' };

    const labelHeightDesktop = getLabelHeight(imagesPerRowDesktop, "desktop");
    const labelHeightTablet = getLabelHeight(imagesPerRowTablet, "tablet");
    const labelHeightPhone = getLabelHeight(imagesPerRowPhone, "phone");

    // dynamically set the grid template columns based on the screen size
    const styleTag = customImagesPerRow ? `
        @media screen and (min-width: 1024px) {
            #${questionModel.logicalName}-imagegrid { 
                grid-template-columns: repeat(${imagesPerRowDesktop}, minmax(0, 1fr));
                --imagegrid-label-height: ${labelHeightDesktop};
            }
        }
        @media screen and (min-width: 600px) and (max-width: 1023px) {
            #${questionModel.logicalName}-imagegrid { 
                grid-template-columns: repeat(${imagesPerRowTablet}, minmax(0, 1fr));
                --imagegrid-label-height: ${labelHeightTablet};
            }
        }
        @media screen and (max-width: 599px) {
            #${questionModel.logicalName}-imagegrid { 
                grid-template-columns: repeat(${imagesPerRowPhone}, minmax(0, 1fr));
                --imagegrid-label-height: ${labelHeightPhone};
            }
        }
    ` : '';

    const handleChange = (value: string) => {
        setSelectedValue(value);
        answerQuestion(questionModel.logicalName, value);
    };
    return (
        <>
            {customImagesPerRow && <style>{styleTag}</style>}
            <div
                id={`${questionModel.logicalName}-imagegrid`}
                style={gridStyle}
                className={styles.grid}>
                {Object.entries(options).map(([key, value]) => (
                    <div key={`button-key-${key}`}
                        onClick={() => handleChange(key)}
                        className={mergeClasses(styles.gridElement, selectedValue === key ? styles.selected : '')}
                        id={`${questionModel.logicalName}-${value.key}`}>
                        <img className={styles.img} src={value.imageUrl}></img>
                        <button className={mergeClasses(styles.label, selectedValue === key ? styles.selectedlabel : '')}>{value.label}</button>
                    </div>
                ))}
            </div>
        </>
    )
}